import { useCallback, useMemo } from 'react';
import { useWatch } from 'react-hook-form';

import { ValidationItems } from '@kanda-libs/ks-component-ts';
import { Expenditure, Income } from '@kanda-libs/ks-frontend-services';

export interface IncomeExpenseValidationHook {
  selectValidation: ValidationItems;
  amountValidation: ValidationItems;
}

export type IncomeExpenseType = 'income' | 'expense';

export default function useIncomeExpenseValidation(
  index: number,
  variant: IncomeExpenseType,
): IncomeExpenseValidationHook {
  const fieldName = useMemo(
    () =>
      variant === 'income'
        ? 'credit.finance_details.incomes'
        : 'credit.finance_details.expenditures',
    [variant],
  );

  const array = useWatch({ name: fieldName });

  const item = useMemo(() => array?.[index], [array, index]);

  const checkIfAmountPresent = useCallback(
    (type?: string | number | boolean) => {
      const value =
        variant === 'expense' ? (item as Expenditure) : (item as Income);
      const amount = value?.monthly_amount?.amount;
      if (!type && !amount) return true;
      if (!type && amount) return false;
      if (typeof type !== 'string') return false;
      if (type === '' && amount) return false;
      return true;
    },
    [item, variant],
  );

  const checkIfTypePresent = useCallback(
    (amount?: string | number | boolean) => {
      const type =
        variant === 'expense'
          ? (item as Expenditure)?.expenditure_type
          : (item as Income)?.income_type;
      if (!type && !amount) return true;
      if (typeof amount !== 'number') return false;
      if (!type && amount === 0) return true;
      if (type && amount === 0) return false;
      return true;
    },
    [item, variant],
  );

  const selectValidation = useMemo(
    () => ({
      validate: {
        value: {
          amountPresent: (value: string | number | boolean) =>
            checkIfAmountPresent(value),
        },
        message: {
          amountPresent: 'Type is required if amount present',
        },
      },
    }),
    [checkIfAmountPresent],
  );

  const amountValidation = useMemo(
    () => ({
      validate: {
        value: {
          typePresent: (value: string | number | boolean | undefined) =>
            checkIfTypePresent(value),
        },
        message: {
          typePresent: 'Amount is required if type present',
        },
      },
    }),
    [checkIfTypePresent],
  );

  return {
    selectValidation,
    amountValidation,
  };
}
